import React, { useEffect, useState } from 'react';

/**
 * Base sidebar menu renderer. Receives an array of items and delegates
 * navigation to the parent via onNavigate.
 */
const SidebarMenu = ({ items = [], activeId, onNavigate = () => {}, className = '' }) => {
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    const activeGroup = items.find((item) => (
      item.openOnActiveChild !== false
      && Array.isArray(item.children)
      && item.children.some((child) => child.id === activeId)
    ));

    if (!activeGroup) return;

    setOpenGroups((prev) => (
      prev[activeGroup.id] ? prev : { ...prev, [activeGroup.id]: true }
    ));
  }, [activeId, items]);

  const handleClick = (item) => {
    if (!item) return;
    onNavigate(item);
  };

  const toggleGroup = (itemId, isCurrentlyOpen) => {
    setOpenGroups((prev) => ({
      ...prev,
      [itemId]: !isCurrentlyOpen
    }));
  };

  const formatBadge = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return parsed > 99 ? '99+' : String(parsed);
  };

  return (
    <nav className={`sidebar-nav ${className}`.trim()}>
      {items.map((item) => {
        const hasChildren = Array.isArray(item.children) && item.children.length > 0;
        const hasActiveChild = hasChildren && item.children.some((child) => child.id === activeId);
        const shouldOpenOnActiveChild = item.openOnActiveChild !== false;
        const isGroupOpen = hasChildren
          ? (openGroups[item.id] ?? item.defaultOpen ?? (shouldOpenOnActiveChild && hasActiveChild))
          : false;
        const badge = formatBadge(item.badge);

        if (hasChildren) {
          return (
            <div key={item.id} className="nav-group">
              <button
                type="button"
                className={`nav-item nav-group-toggle ${hasActiveChild ? 'active-parent' : ''}`}
                onClick={() => toggleGroup(item.id, isGroupOpen)}
                aria-expanded={isGroupOpen}
              >
                <i className={item.icon}></i>
                <span>{item.label}</span>
                {badge && <span className="nav-item-badge">{badge}</span>}
                <i className={`fas ${isGroupOpen ? 'fa-chevron-down' : 'fa-chevron-right'} nav-group-chevron`}></i>
              </button>
              {isGroupOpen && (
                <div className="nav-submenu">
                  {item.children.map((child) => {
                    const childBadge = formatBadge(child.badge);
                    return (
                      <button
                        key={child.id}
                        type="button"
                        className={`nav-item nav-subitem ${activeId === child.id ? 'active' : ''}`}
                        onClick={() => handleClick(child)}
                      >
                        <i className={child.icon}></i>
                        <span>{child.label}</span>
                        {childBadge && <span className="nav-item-badge">{childBadge}</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        }

        return (
          <button
            key={item.id}
            type="button"
            className={`nav-item ${activeId === item.id ? 'active' : ''}`}
            onClick={() => handleClick(item)}
          >
            <i className={item.icon}></i>
            <span>{item.label}</span>
            {badge && <span className="nav-item-badge">{badge}</span>}
          </button>
        );
      })}
    </nav>
  );
};

export default SidebarMenu;
